import { Card } from '../../../molecules/Card/Card';
import { legendItems, type ChartSeries } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { WordCloud, type WordCloudWord } from '../../../organisms/WordCloud/WordCloud';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';
import { CLOUD_WORDS } from '../TrendingKeywordsCard/TrendingKeywordsCard';

export type TrendingTopicsView = {
  /**
   * Every topic in scope, ranked by mentions, coloured by dominant sentiment.
   * Topics pass no gloss: the label is already the English word.
   */
  words: WordCloudWord[];
  /** The sentiments present, for the legend. */
  series: ChartSeries[];
  formatValue: (value: number) => string;
};

export type TrendingTopicsCardProps = WidgetBlockProps & {
  view?: TrendingTopicsView;
  /** Opens the conversations behind a word (its key, which is its label). */
  onViewDetails?: (key: string) => void;
};

/**
 * Figma `947:19576` — the subjects generating the most conversation.
 *
 * Not `limit`ed upstream, because the cloud does its own capping — and it has
 * to, since `sizeStep` is pinned to the largest word and trimming before the
 * cloud sees the list would not change which word that is.
 *
 * No `kpi`, which is the frame's own edit — both `KPI` instances were deleted
 * from it on 2026-09-17. `Card` renders the KPI row only when one is passed,
 * so the slot closes rather than standing empty.
 */
export function TrendingTopicsCard({
  status,
  view,
  span,
  chrome,
  onRetry,
  onViewDetails,
}: TrendingTopicsCardProps) {
  const hasData = view !== undefined && view.words.length > 0;
  return (
    <Card
      {...chrome}
      className={spanClass(span)}
      emptyLabel="No conversation in this period"
      {...cardState(status, hasData, onRetry)}
      title="Trending topics"
      helpText="The subjects generating the most conversation right now, sized by mention volume and coloured by the sentiment most of those mentions carry."
      helpSupportingText="Colour is a plurality, not a majority: whichever of the three tones is largest paints the word, so a green word can still be a third negative. Hover one for the split behind it."
      chart={
        view && (
          <WordCloud
            onViewDetails={onViewDetails}
            words={view.words}
            limit={CLOUD_WORDS}
            height={PLOT_HEIGHT}
            unit="mentions"
            emptyLabel="No conversation in this period"
            formatValue={view.formatValue}
          />
        )
      }
      legend={view && <ChartLegend items={legendItems(view.series)} />}
    />
  );
}
